import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../../lib/cn.ts'
import { useMediaQuery } from '../../../lib/useMediaQuery.ts'

/** At or below this width the popover is a bottom sheet instead of an anchored panel. */
export const SHEET_QUERY = '(max-width: 480px)'

const GAP = 6
const EDGE = 8

export interface PopoverProps {
  /** What the popover hangs off; focus returns here when it closes. */
  anchor: RefObject<HTMLElement | null>
  onClose: () => void
  /** Accessible name of the dialog. */
  label: string
  /** Panel width on wider screens (px). */
  width?: number
  /** Tallest the anchored panel gets (px); it also shrinks to the room left in the viewport. */
  maxHeight?: number
  /** Gets focus on close when the anchor has unmounted (its chip was removed). */
  returnFocus?: RefObject<HTMLElement | null>
  className?: string
  children: ReactNode
}

/**
 * Small non-modal popover (`role=dialog`) anchored below an element, kept inside the viewport
 * (flips above when there's no room). On phones it's a bottom sheet with a scrim. Closes on Esc
 * and on a pointer-down outside, and puts focus back on the anchor. Rendered in a portal so
 * `overflow: hidden` ancestors don't clip it.
 */
export function Popover({
  anchor,
  onClose,
  label,
  width = 280,
  maxHeight = 420,
  returnFocus,
  className,
  children,
}: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null)
  const sheet = useMediaQuery(SHEET_QUERY)
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  const place = useCallback(() => {
    // The anchor can unmount while open (its chip was removed): stay where we are.
    const el = anchor.current
    const panel = ref.current
    if (!el?.isConnected || !panel) return
    const a = el.getBoundingClientRect()
    const vw = document.documentElement.clientWidth
    const vh = window.innerHeight
    const w = Math.min(width, vw - EDGE * 2)
    const left = Math.max(EDGE, Math.min(a.left, vw - w - EDGE))
    const below = Math.min(maxHeight, vh - a.bottom - GAP - EDGE)
    const above = Math.min(maxHeight, a.top - GAP - EDGE)
    const natural = panel.scrollHeight
    const next =
      below >= Math.min(natural, 320) || below >= above
        ? { top: a.bottom + GAP, left, maxHeight: below }
        : { top: a.top - GAP - Math.min(natural, above), left, maxHeight: above }
    setPos((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.maxHeight === next.maxHeight
        ? prev
        : next,
    )
  }, [anchor, width, maxHeight])

  useLayoutEffect(() => {
    if (sheet) return
    place()
    const panel = ref.current
    const ro = panel ? new ResizeObserver(() => place()) : null
    if (panel) ro?.observe(panel)
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [place, sheet])

  // Follow the anchor when the content around it changes (a chip added before it, say).
  useLayoutEffect(() => {
    if (!sheet) place()
  })

  // Outside pointer-down closes (the anchor toggles itself, so it's not "outside").
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t) || anchor.current?.contains(t)) return
      closeRef.current()
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
  }, [anchor])

  // Focus back to the opener when closing (if focus was inside, or nowhere).
  // biome-ignore lint/correctness/useExhaustiveDependencies: unmount only
  useEffect(() => {
    return () => {
      const active = document.activeElement
      const inside = !active || active === document.body || ref.current?.contains(active)
      const target = anchor.current?.isConnected ? anchor.current : returnFocus?.current
      if (inside && target?.isConnected) target.focus({ preventScroll: true })
    }
  }, [])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      closeRef.current()
    }
  }

  const panel = (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      onKeyDown={onKeyDown}
      style={
        sheet
          ? undefined
          : {
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              width: `min(${width}px, calc(100vw - ${EDGE * 2}px))`,
              maxHeight: pos?.maxHeight,
            }
      }
      className={cn(
        'fixed z-50 flex flex-col overflow-hidden border border-border bg-surface text-text shadow-pop',
        sheet
          ? 'inset-x-0 bottom-0 max-h-[75dvh] animate-fade rounded-t-modal pb-[env(safe-area-inset-bottom)]'
          : 'animate-fade rounded-panel',
        className,
      )}
    >
      {children}
    </div>
  )

  return createPortal(
    <>
      {sheet && <div aria-hidden className="fixed inset-0 z-50 bg-scrim animate-fade" />}
      {panel}
    </>,
    document.body,
  )
}
